import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './features/auth/pages/Login';
import ForgotPassword from './features/auth/pages/ForgotPassword';
import ResetPassword from './features/auth/pages/ResetPassword';
import Layout from './components/layout/Layout';
import RequireRole from './components/auth/RequireRole';
import Dashboard from './features/dashboard/pages/Dashboard';
import SalesDashboard from './features/dashboard/pages/SalesDashboard';
import DesignerDashboard from './features/dashboard/pages/DesignerDashboard';
import OperatorDashboard from './features/dashboard/pages/OperatorDashboard';
import Jobs from './features/jobs/pages/Jobs';
import JobForm from './features/jobs/pages/JobForm';
import JobDetail from './features/jobs/pages/JobDetail';
import Expenses from './features/expenses/pages/Expenses';
import Services from './features/services/pages/Services';
import Customers from './features/customers/pages/Customers';
import Users from './features/users/pages/Users';
import Reports from './features/reports/pages/Reports';
import StockManagement from './features/stock/pages/StockManagement';
import Documents from './features/documents/pages/Documents';
import Profile from './features/profile/pages/Profile';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ADMIN_MANAGER, SALES_AND_UP } from './config/roles';

function guard(roles, element) {
  return <RequireRole roles={roles}>{element}</RequireRole>;
}

function App() {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const role = user?.role;
  const homeByRole = role === 'admin' || role === 'manager'
    ? <Dashboard />
    : role === 'sales_manager'
      ? <SalesDashboard />
      : role === 'designer'
        ? <DesignerDashboard />
        : <OperatorDashboard />;

  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={homeByRole} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/new" element={guard(SALES_AND_UP, <JobForm />)} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/jobs/:id/edit" element={guard(SALES_AND_UP, <JobForm />)} />

        <Route path="/expenses" element={guard(ADMIN_MANAGER, <Expenses />)} />
        <Route path="/expenses/new" element={<Navigate to="/expenses" replace />} />
        <Route path="/expenses/:id/edit" element={<Navigate to="/expenses" replace />} />

        <Route path="/services" element={guard(ADMIN_MANAGER, <Services />)} />
        <Route path="/services/new" element={<Navigate to="/services" replace />} />
        <Route path="/services/:id/edit" element={<Navigate to="/services" replace />} />

        <Route path="/customers" element={guard(SALES_AND_UP, <Customers />)} />
        <Route path="/customers/new" element={<Navigate to="/customers" replace />} />
        <Route path="/customers/:id/edit" element={<Navigate to="/customers" replace />} />

        <Route path="/users" element={guard(ADMIN_MANAGER, <Users />)} />
        <Route path="/users/new" element={<Navigate to="/users" replace />} />
        <Route path="/users/:id/edit" element={<Navigate to="/users" replace />} />

        <Route path="/reports" element={guard(SALES_AND_UP, <Reports />)} />
        <Route path="/stock" element={guard(SALES_AND_UP, <StockManagement />)} />
        <Route path="/documents" element={guard(SALES_AND_UP, <Documents />)} />

        <Route path="/profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
